import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import swal from "sweetalert";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DateRangePicker from "react-bootstrap-daterangepicker";
import '../../css/SearchFilterPanel.css';
import './tasklist.css';
import '../Documents/commonstyle.css';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { hasFeatureAccess } from '../../utils/featureLock';
import DateInput from '../components/Forms/DateInput';
import { getSavedViewMode, saveViewMode } from '../../utils/viewPersistence';
import CommonTable from '../components/CommonTable/CommonTable';

// Debounce utility
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

/** Parse YYYY-MM-DD to local Date (date-only, no UTC shift). */
function parseYmd(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function toYmd(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

// Shared date formatting for task list API (YYYY-MM-DD, local)
function formatDateLocalForApi(date) {
  if (!date) return '';
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return date;
}

function mapTaskListApiRow(task, formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY) {
  return {
    TasksId: task.id,
    TaskName: task.title,
    description: task.description,
    project_id: task.project_id,
    status: task.status,
    Priority: task.priority,
    start_date: task.start_date,
    due_date: task.due_date,
    corporate_id: task.corporate_id,
    created_by: task.created_by,
    created_at: task.created_at,
    updated_at: task.updated_at,
    parent_task: task.parent_task,
    is_repeatable: task.is_repeatable,
    repeated_from_task_id: task.repeated_from_task_id,
    repeat_type: task.repeat_type,
    repeat_until: task.repeat_until,
    delta: task.delta,
    is_assignee_update_allowed: task.is_assignee_update_allowed,
    CreatedAt: formatDateTimeToDDMMMYYYY(task.created_at, true),
    StartDate: formatDateToDDMMMYYYY(task.start_date, false),
    EndDate: formatDateToDDMMMYYYY(task.due_date, false),
    CompletedAt: task.completed_at ? formatDateTimeToDDMMMYYYY(task.completed_at, true) : null,
    TaskMembers: task.TaskMembers || [],
    Assignees: task.Assignees
      ? task.Assignees.map((assignee) => ({
          CorporateUserId: assignee.CorporateUserId,
          FullName: assignee.FullName,
          Email: assignee.Email,
        }))
      : [],
    Observers: task.Observers || [],
    Mentions: task.Mentions || [],
    image: '/images/task/img1.jpg',
    Departments: task.TaskDepartments || [],
    Branches: task.TaskBranches || [],
    Department: task.Department || [],
    subDepartments: task.subDepartments || [],
    departments: task.department || task.TaskDepartments || [],
    branches: task.branch || task.TaskBranches || [],
  };
}

const TaskList = () => {
  const quickFilters = ['Low', 'Medium', 'High'];
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listSearchString } = location.state || {};

  const [tasks, setTasks] = useState([]);
  const [tasklist, settasklist] = useState([])

  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const [Projects, setProjects] = useState([])
  const [searchDropDown, setSearchDropDown] = useState(false)
  const [filters, setFilters] = useState(() => ({
    status: searchParams.get('status') || '',
    startDateFrom: parseYmd(searchParams.get('sdf')),
    startDateTo: parseYmd(searchParams.get('sdt')),
    endDateFrom: parseYmd(searchParams.get('edf')),
    endDateTo: parseYmd(searchParams.get('edt')),
    Priority: '',
    ProjectId: searchParams.get('projectId') || '',
    only_my_task: searchParams.get('my') === '1',
  }));
  // Include URL ?search= on first render so the initial fetch matches history when returning
  // from task details (location.state is often missing on browser back even though the URL is correct).
  const [searchText, setSearchText] = useState(
    () => listSearchString || searchParams.get('search') || ''
  );
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(
    () => searchParams.get('qp') || ''
  );
  const [sortBy, setSortBy] = useState(() => searchParams.get('sort') || 'rank');
  const [corporateUsers, setCorporateUsers] = useState([])
  const debouncedSearchText = useDebounce(searchText, 500);
  const statusMap = {
    'Pending': 0,
    'In Progress': 1,
    'Blocked': 2,
    'Completed': 3,
    'Paused': 4,
    'Partial': 5,
  };
  const filterRef = useRef(null);
  const [reminderList, setReminderList] = useState([])
  const filterUrlSyncSkipRef = useRef(true);

  const getTaskListApiQuery = (pageNum, limit, searchStr) => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(limit),
    });
    if (searchStr) params.append('search', searchStr);
    if (sortBy) params.append('sort', sortBy);
    const numericStatus = statusMap[filters.status] ?? '';
    if (numericStatus !== '') params.append('status', String(numericStatus));
    if (filters.startDateFrom) {
      params.append('start_date_from', formatDateLocalForApi(filters.startDateFrom));
    }
    if (filters.startDateTo) {
      params.append('start_date_to', formatDateLocalForApi(filters.startDateTo));
    }
    if (filters.endDateFrom) {
      params.append('end_date_from', formatDateLocalForApi(filters.endDateFrom));
    }
    if (filters.endDateTo) {
      params.append('end_date_to', formatDateLocalForApi(filters.endDateTo));
    }
    const projectId = searchParams.get('projectId') || filters.ProjectId;
    if (projectId) params.append('project_id', projectId);
    if (selectedQuickFilter) params.append('priority', selectedQuickFilter);
    if (filters.only_my_task) params.append('only_my_task', 'true');
    params.append('CorporateId', String(parseInt(userData.corporateId, 10)));
    return params;
  };

  const commitTaskListToUrl = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (searchText) p.set('search', searchText);
      else p.delete('search');
      if (sortBy) p.set('sort', sortBy);
      else p.delete('sort');
      if (filters.status) p.set('status', filters.status);
      else p.delete('status');
      if (filters.startDateFrom) p.set('sdf', toYmd(filters.startDateFrom));
      else p.delete('sdf');
      if (filters.startDateTo) p.set('sdt', toYmd(filters.startDateTo));
      else p.delete('sdt');
      if (filters.endDateFrom) p.set('edf', toYmd(filters.endDateFrom));
      else p.delete('edf');
      if (filters.endDateTo) p.set('edt', toYmd(filters.endDateTo));
      else p.delete('edt');
      if (filters.ProjectId) p.set('projectId', String(filters.ProjectId));
      else p.delete('projectId');
      if (selectedQuickFilter) p.set('qp', selectedQuickFilter);
      else p.delete('qp');
      if (filters.only_my_task) p.set('my', '1');
      else p.delete('my');
      return p;
    }, { replace: true });
  };

  /** Full query for navigation off the list: uses live state so filters apply even if not yet re-committed to the URL. */
  const getTaskListQuerySuffix = () => {
    const p = new URLSearchParams();
    if (searchText) p.set('search', searchText);
    if (sortBy) p.set('sort', sortBy);
    if (filters.status) p.set('status', filters.status);
    if (filters.startDateFrom) p.set('sdf', toYmd(filters.startDateFrom));
    if (filters.startDateTo) p.set('sdt', toYmd(filters.startDateTo));
    if (filters.endDateFrom) p.set('edf', toYmd(filters.endDateFrom));
    if (filters.endDateTo) p.set('edt', toYmd(filters.endDateTo));
    if (filters.ProjectId) p.set('projectId', String(filters.ProjectId));
    if (selectedQuickFilter) p.set('qp', selectedQuickFilter);
    if (filters.only_my_task) p.set('my', '1');
    const s = p.toString();
    return s ? `?${s}` : '';
  };

  // Helper functions for list view
  const getStatusClass = (status) => {
    const statusValue = typeof status === 'number' ? status : parseInt(status);
    switch (statusValue) {
      case 3:
        return 'status-done';
      case 1:
        return 'status-in-progress';
      case 2:
        return 'status-postponed';
      case 0:
        return 'status-to-do';
      case 4:
        return 'status-paused';
      case 5:
        return 'status-in-partial';
      default:
        return 'status-to-do';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const getStatusLabel = (status) => {
    const statusValue = parseInt(status);
    switch (statusValue) {
      case 3:
        return 'Completed';
      case 1:
        return 'In Progress';
      case 2:
        return 'Blocked';
      case 0:
        return 'Pending';
      case 4:
        return 'Paused';
      case 5:
        return 'Partial';
      default:
        return 'Pending';
    }
  };
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [availableAssignees, setAvailableAssignees] = useState([])
  const [hasAddTaskAccess, setHasAddTaskAccess] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('tasklist', 'card')); // 'card' or 'list'
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTaskData, setDeleteTaskData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const hasInitialLoad = useRef(false); // Track if initial data has been loaded
  const isFetchingRef = useRef(false); // Track if fetch is in progress
  const prevDebouncedSearchRef = useRef(null); // Track previous debounced search value
  const prevSortByRef = useRef(null); // Track previous sortBy value
  
  // Handle view mode change and save to localStorage
  const handleViewModeChange = (newViewMode) => {
    setViewMode(newViewMode);
    saveViewMode('tasklist', newViewMode);
  };
  
  const notifyError = (data) => {

    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };
  // Check feature access on component mount
  useEffect(() => {
    const checkFeatureAccess = async () => {
      try {
        setFeatureLoading(true);
        const taskAccess = await hasFeatureAccess('adding_tasks');
        setHasAddTaskAccess(taskAccess);
      } catch (error) {
        console.error('Error checking feature access:', error);
        setHasAddTaskAccess(false);
      } finally {
        setFeatureLoading(false);
      }
    };

    checkFeatureAccess();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      const userData = JSON.parse(localStorage.getItem("user"))
      const params = new URLSearchParams();

      try {
        if (userData?.corporateId) {
          params.append('corporateId', parseInt(userData.corporateId))
          
          // Add branch and department parameters if available
          // if (userData.branchId) {
          //   params.append('branch', userData.branchId);
          // }
          // if (userData.departmentId) {
          //   params.append('department', userData.departmentId);
          // }

          params.append('page', 1);
          params.append('limit', 10000);
          
          const res = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
          const data = res.data;
          // Filter out the currently logged-in user
          const filteredUsers = (data.users || []).filter(user => user.id !== userData.id);
          setAvailableAssignees(filteredUsers); 
          setCorporateUsers(data.users);
          // Store in localStorage for backward compatibility if needed
          localStorage.setItem('assigneess', JSON.stringify(filteredUsers));
        }
      } catch (error) {
        console.error("User list fetch error:", error);
      } finally {
        setLoading(false);
      }

    };

    const loadData = async () => {
      await fetchUsers();
    };

    loadData();
  }, []);
  const fetchData = async (pageNum = 1, append = false, search = '') => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return false;
    }
    
    try {
      isFetchingRef.current = true;
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      const params = getTaskListApiQuery(pageNum, 10, search);
      const res = await axiosInstance.get(`/task/list?${params.toString()}`);
      const result = res.data;

      const formattedTasks = (result.tasks || []).map((task) =>
        mapTaskListApiRow(task, formatDateTimeToDDMMMYYYY, formatDateToDDMMMYYYY)
      );

      if (append) {
        setTasks(prev => [...prev, ...formattedTasks]);
        settasklist(prev => [...prev, ...formattedTasks]);
      } else {
        setTasks(formattedTasks);
        settasklist(formattedTasks);
      }
      setHasMore(formattedTasks.length === 10);
      setPage(pageNum);
      return true;
    } catch (error) {
      setError(error.message);
      
      // Handle 403 Forbidden error
      if (error.response && error.response.status === 409) {
        notifyError("Access denied. You don't have permission to access task data.");
        // Clear the task list and show empty state
        setTasks([]);
        settasklist([]);
      }
      return false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  };



  useEffect(() => {
    fetchProjects();
  }, []);
  // Sync searchText with query parameter on mount and when it changes
  useEffect(() => {
    const querySearch = searchParams.get('search') || '';
    if (querySearch !== searchText) {
      setSearchText(querySearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filterQueryKeyForSync = useMemo(() => {
    return [
      searchParams.get('status') || '',
      searchParams.get('sdf') || '',
      searchParams.get('sdt') || '',
      searchParams.get('edf') || '',
      searchParams.get('edt') || '',
      searchParams.get('projectId') || '',
      searchParams.get('qp') || '',
      searchParams.get('my') || '',
      searchParams.get('sort') || '',
    ].join('|');
  }, [searchParams]);

  useEffect(() => {
    if (filterUrlSyncSkipRef.current) {
      filterUrlSyncSkipRef.current = false;
      return;
    }
    const nextSort = searchParams.get('sort') || 'rank';
    setFilters({
      status: searchParams.get('status') || '',
      startDateFrom: parseYmd(searchParams.get('sdf')),
      startDateTo: parseYmd(searchParams.get('sdt')),
      endDateFrom: parseYmd(searchParams.get('edf')),
      endDateTo: parseYmd(searchParams.get('edt')),
      Priority: '',
      ProjectId: searchParams.get('projectId') || '',
      only_my_task: searchParams.get('my') === '1',
    });
    setSelectedQuickFilter(searchParams.get('qp') || '');
    setSortBy(nextSort);
  }, [filterQueryKeyForSync]);

  // Initial data load - only once on mount
  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      // Initialize refs with current values
      prevDebouncedSearchRef.current = debouncedSearchText;
      prevSortByRef.current = sortBy;
      // Use searchText which may have been set from URL params or listSearchString
      fetchData(1, false, searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when search text changes (debounced) - but not on initial mount
  useEffect(() => {
    // Skip if not yet loaded initially
    if (!hasInitialLoad.current) return;
    
    // Only fetch if debouncedSearchText actually changed (not just initialized)
    if (prevDebouncedSearchRef.current !== debouncedSearchText) {
      prevDebouncedSearchRef.current = debouncedSearchText;
      fetchData(1, false, debouncedSearchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  // Fetch data when sort options change - but not on initial mount
  useEffect(() => {
    // Skip if not yet loaded initially
    if (!hasInitialLoad.current) return;
    
    // Only fetch if sortBy actually changed (not just initialized)
    if (prevSortByRef.current !== sortBy && sortBy) {
      prevSortByRef.current = sortBy;
      fetchData(1, false, searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const fetchProjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData?.corporateId) {


        const queryParams = new URLSearchParams();

        queryParams.append('corporateId', userData.corporateId);


        const response = await axiosInstance.get(
          `/project/list?${queryParams.toString()}`
        );
        setProjects(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      
      // Handle 403 Forbidden error
      if (error.response && error.response.status === 409) {
        notifyError("Access denied. You don't have permission to access project data.");
        return;
      }
      
      notifyError(error)
    }
  };

  const handleClickOutside = (event) => {
    // Don't close filter if click happens inside the daterangepicker overlay
    const clickedInsideDatePicker = !!(event.target.closest && (
      event.target.closest('.daterangepicker') ||
      event.target.closest('.drp-buttons') ||
      event.target.closest('.calendar-table')
    ));

    if (filterRef.current && !filterRef.current.contains(event.target) && !clickedInsideDatePicker) {
      setSearchDropDown(false);
      // Don't reset filters or tasks when closing dropdown
      // Only close the dropdown
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };

  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      // Check if click is outside any dropdown (both table and card views)
      if (openDropdownId && 
          !event.target.closest('.table-actions') && 
          !event.target.closest('.task-dropdown')) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutsideDropdown);
      return () => {
        document.removeEventListener('mousedown', handleClickOutsideDropdown);
      };
    }
  }, [openDropdownId]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFilters({ ...filters, [e.target.name]: value });
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('sort', value);
      return p;
    }, { replace: true });
  };

  const resetFilters = async () => {
    const contextProjectId = searchParams.get('projectId') || '';
    setFilters({
      status: '',
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
      Priority: '',
      ProjectId: contextProjectId,
      only_my_task: false,
    });
    setSelectedQuickFilter('');
    prevSortByRef.current = 'creation';
    setSortBy('creation');
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      ['status', 'sdf', 'sdt', 'edf', 'edt', 'qp', 'my'].forEach((k) => p.delete(k));
      p.set('sort', 'creation');
      if (searchText) p.set('search', searchText);
      else p.delete('search');
      if (contextProjectId) p.set('projectId', contextProjectId);
      else p.delete('projectId');
      return p;
    }, { replace: true });
    await fetchData(1, false, searchText);
  };

  const handleSearch = async () => {
    const ok = await fetchData(1, false, searchText);
    if (ok) {
      commitTaskListToUrl();
      setSearchDropDown(false);
    }
  };

  const handleEdit = (event, task) => {
    // If it's a repeat task instance, show confirmation modal
    if (task?.repeated_from_task_id) {
      setEditTaskData(task);
      setShowEditModal(true);
    } else if (task?.is_repeatable) {
      // If it's a root repeatable task, show warning that editing affects all instances
      setEditTaskData(task);
      setShowEditModal(true);
    } else {
      // Normal edit - navigate directly
      let taskid = task?.TasksId;
      navigate(`/task/edittask${getTaskListQuerySuffix()}`, {
        state: { taskid, listSearchString: searchText },
      });
    }
  };

  const handleEditInstance = () => {
    if (!editTaskData) return;
    const taskid = editTaskData?.TasksId;
    setShowEditModal(false);
    navigate(`/task/edittask${getTaskListQuerySuffix()}`, {
      state: { taskid, listSearchString: searchText },
    });
    setEditTaskData(null);
  };

  const handleEditSeries = () => {
    if (!editTaskData) return;
    // Use repeated_from_task_id for editing entire series (if it's an instance)
    // Or use the task's own ID if it's a root task
    const taskid = editTaskData.repeated_from_task_id || editTaskData.TasksId;
    setShowEditModal(false);
    navigate(`/task/edittask${getTaskListQuerySuffix()}`, {
      state: { taskid, listSearchString: searchText },
    });
    setEditTaskData(null);
  };
  const TaskUpdate = (event, task) => {
    let taskid = task?.TasksId
    event.stopPropagation();
    navigate(`/task/taskdetails/${taskid}${getTaskListQuerySuffix()}`);
  };
  const handleDeleteWithConfirmation = (task) => {
    setDeleteTaskData(task);
    setShowDeleteModal(true);
  };

  const handleDeleteInstance = async () => {
    if (!deleteTaskData) return;
    const taskId = deleteTaskData?.TasksId;
    const isRootTask = deleteTaskData?.is_repeatable && !deleteTaskData?.repeated_from_task_id;
    setShowDeleteModal(false);
    
    try {
      let deleteResult = await handleDelete(taskId, false, isRootTask);
      if (deleteResult === 'deleted') {
        toast.success(isRootTask ? "Task deleted successfully" : "Task instance deleted successfully");
      } else {
        notifyError("Error in deleting task instance");
      }
    } catch (err) {
      notifyError("An unexpected error occurred");
      console.error(err);
    } finally {
      setDeleteTaskData(null);
    }
  };

  const handleDeleteSeries = async () => {
    if (!deleteTaskData) return;
    // For instance: use repeated_from_task_id (root id); for root task: use TasksId (root is the series)
    const seriesTaskId = deleteTaskData.repeated_from_task_id ?? deleteTaskData.TasksId;
    setShowDeleteModal(false);
    
    try {
      let deleteResult = await handleDelete(seriesTaskId, true);
      if (deleteResult === 'deleted') {
        toast.success("Repeat task series deleted successfully");
      } else {
        notifyError("Error in deleting task series");
      }
    } catch (err) {
      notifyError("An unexpected error occurred");
      console.error(err);
    } finally {
      setDeleteTaskData(null);
    }
  };

  const handleDeleteNormal = async () => {
    if (!deleteTaskData) return;
    const taskId = deleteTaskData?.TasksId || deleteTaskData;
    const isRootTask = deleteTaskData?.is_repeatable && !deleteTaskData?.repeated_from_task_id;
    setShowDeleteModal(false);
    
    try {
      // If it's a root repeatable task, delete the entire series
      let result = await handleDelete(taskId, isRootTask);
      if (result === 'deleted') {
        if (isRootTask) {
          toast.success("Repeat task series deleted successfully");
        } else {
          toast.success("Task deleted successfully");
        }
      } else {
        notifyError("Error in deleting");
      }
    } catch (err) {
      notifyError("An unexpected error occurred");
      console.error(err);
    } finally {
      setDeleteTaskData(null);
    }
  };

  const handleDelete = async (taskid, deleteSeries = false, deleteFirstInstanceOnly = false) => {
    try {
      const data = { taskId: taskid };
      if (deleteFirstInstanceOnly) data.deleteFirstInstanceOnly = true;
      const res = await axiosInstance.delete('/task/deletetask', { data });
      if (res) {
        if (deleteSeries) {
          // Remove all tasks that are instances of this series
          setTasks(tasks.filter((p) => p.repeated_from_task_id !== taskid && p.TasksId !== taskid));
        } else {
          // Remove only the specific task (or root when deleteFirstInstanceOnly)
          setTasks(tasks.filter((p) => p.TasksId !== taskid));
        }
        return 'deleted';
      }
    } catch (error) {
      console.error('Delete error:', error);
      notifyError(error.response?.data?.message || error.message || "Error deleting task");
      throw error;
    }
  };

  const handleAddTask = (e) => {
    e?.preventDefault?.();
    if (!hasAddTaskAccess) {
      toast.error('Adding tasks is not available in your current plan. Please upgrade your subscription.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    navigate(`/task/addtask${getTaskListQuerySuffix()}`, { state: { listSearchString: searchText } });
  };

  useEffect(() => {
    if (viewMode === 'list') {
      // For table view, only use table wrapper scroll (not window scroll)
      const handleTableScroll = (event) => {
        const threshold = 5;
        const wrapper = event.target;
        if (wrapper && wrapper.classList.contains('table-wrapper')) {
          const isNearBottom = wrapper.scrollHeight - wrapper.scrollTop <= wrapper.clientHeight + threshold;
          if (isNearBottom && hasMore && !loadingMore && !loading) {
            fetchData(page + 1, true, searchText);
          }
        }
      };

      const wrapper = document.querySelector('.table-wrapper');
      if (wrapper) {
        wrapper.addEventListener('scroll', handleTableScroll);
        return () => {
          wrapper.removeEventListener('scroll', handleTableScroll);
        };
      }
      // If wrapper not available yet, don't attach listener
      return () => {};
    } else {
      // For card view, use window scroll to trigger pagination
      const handleWindowScroll = () => {
        const threshold = 5;
        const isNearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold;
        if (isNearBottom && hasMore && !loadingMore && !loading) {
          fetchData(page + 1, true, searchText);
        }
      };

      window.addEventListener('scroll', handleWindowScroll);
      return () => {
        window.removeEventListener('scroll', handleWindowScroll);
      };
    }
  }, [hasMore, loadingMore, loading, page, viewMode, tasks, searchText]);

  // Helper function to check if task is overdue
  const isTaskOverdue = (task) => {
    // Task is overdue if due date is less than today and status is not 3 (Completed)
    if (task.status === 3) {
      return false; // Completed tasks are never overdue
    }
    
    if (!task.due_date) {
      return false; // No due date means not overdue
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    return dueDate < today;
  };

  // Helper function to get creator name
  const getCreatorName = (task) => {
    // First try to find creator in TaskMembers
    const creatorMember = task.TaskMembers?.find(member => member.role === 'creator');
    if (creatorMember?.user.full_name) {
      return creatorMember.user.full_name;
    }
    
    // If not found, try to find by created_by ID in Assignees or TaskMembers
    if (task.created_by) {
      const creatorById = task.Assignees?.find(assignee => 
        assignee.CorporateUserId == task.created_by
      );
      if (creatorById?.FullName) {
        return creatorById.FullName;
      }
      
      const creatorByTaskMemberId = corporateUsers.find(user => user.id == task.created_by);
      if (creatorByTaskMemberId?.full_name) {
        return creatorByTaskMemberId.full_name;
      }
    }
    
    return 'Unknown';
  };

  // Helper function to get assignees names (including departments, branches, sub-departments)
  const getAssigneesNames = (task) => {
    const assigneeTypes = [];
    
    // Get individual user assignees (excluding creator)
    if (task.Assignees && task.Assignees.length > 0) {
      const userAssignees = task.Assignees.filter(assignee => {
        const isCreator = task.TaskMembers?.find(member => 
          member.user_id == assignee.CorporateUserId && member.role === 'creator'
        );
        return !isCreator && assignee.FullName;
      });
      
      if (userAssignees.length > 0) {
        assigneeTypes.push(userAssignees.map(assignee => assignee.FullName).join(', '));
      }
    }
    
    // Get department assignees - check all possible field structures
    const departments = task.Departments || task.department || task.TaskDepartments || [];
    if (departments && departments.length > 0) {
      const deptAssignees = departments
        .filter(dept => dept && dept.is_assignee === true)
        .map(dept => {
          if (dept.Department) {
            return dept.Department.name || dept.Department.department_name;
          } else if (dept.name) {
            return dept.name;
          } else if (dept.department_name) {
            return dept.department_name;
          }
          return null;
        })
        .filter(Boolean);
      
      if (deptAssignees.length > 0) {
        assigneeTypes.push(`Dept: ${deptAssignees.join(', ')}`);
      }
    }
    
    // Get branch assignees - check all possible field structures
    const branches = task.Branches || task.branch || task.TaskBranches || [];
    if (branches && branches.length > 0) {
      const branchAssignees = branches
        .filter(branch => branch && branch.is_assignee === true)
        .map(branch => {
          if (branch.Branch) {
            return branch.Branch.name;
          } else if (branch.name) {
            return branch.name;
          } else if (branch.branch_name) {
            return branch.branch_name;
          }
          return null;
        })
        .filter(Boolean);
      
      if (branchAssignees.length > 0) {
        assigneeTypes.push(`Branch: ${branchAssignees.join(', ')}`);
      }
    }
    
    // Get sub-department assignees
    if (task.subDepartments && task.subDepartments.length > 0) {
      const subDeptAssignees = task.subDepartments
        .filter(subDept => subDept.is_assignee === true)
        .map(subDept => subDept.name)
        .filter(Boolean);
      
      if (subDeptAssignees.length > 0) {
        assigneeTypes.push(`Sub-Dept: ${subDeptAssignees.join(', ')}`);
      }
    }
    
    return assigneeTypes.length > 0 ? assigneeTypes.join(' | ') : 'None';
  };

  // Helper function to get department names
  const getDepartmentNames = (task) => {
    if (!task.Departments || task.Departments.length === 0) {
      return 'None';
    }
    
    const departmentNames = task.Departments
      .map(dept => dept.Department?.department_name || dept.Department?.name)
      .filter(Boolean);
    
    return departmentNames.length > 0 ? departmentNames.join(', ') : 'None';
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'task',
      header: 'Task',
      render: (row) => (
        <div className="task-title clickable-title">
          <div className="task-id-priority-line">
            <span className="task-id-small">#{row.TasksId}</span>
            <span className="task-priority">({row.Priority || 'Low'})</span>
          </div>
          <span>
            {row.TaskName}
          </span>
        </div>
      ),
      cellClassName: 'task-cell',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`status-badge ${getStatusClass(row.status)}`}>
          {getStatusLabel(row.status)}
        </span>
      ),
      cellClassName: 'status-cell',
    },
    {
      key: 'creator',
      header: 'Creator',
      render: (row) => (
        <div className="user-name">{getCreatorName(row)}</div>
      ),
      cellClassName: 'user-cell',
    },
    {
      key: 'assignees',
      header: 'Assignees',
      render: (row) => (
        <div className="assignees-list" title={getAssigneesNames(row)}>
          {getAssigneesNames(row)}
        </div>
      ),
      cellClassName: 'user-cell',
    },
    {
      key: 'department',
      header: 'Department',
      render: (row) => (
        <div className="department-name">
          {getDepartmentNames(row)}
        </div>
      ),
      cellClassName: 'department-cell',
    },
    {
      key: 'startDate',
      header: 'Task Date',
      accessor: (row) => row.StartDate || 'N/A',
      cellClassName: 'date-cell',
    },
    {
      key: 'dueDate',
      header: 'End Date',
      render: (row) => (
        <div className={isTaskOverdue(row) ? 'overdue-date' : ''}>
          {row.EndDate || 'N/A'}
          {isTaskOverdue(row) && (
            <span className="overdue-indicator" title="This task is overdue">
              <i className="fas fa-exclamation-triangle ms-1"></i>
            </span>
          )}
        </div>
      ),
      cellClassName: 'date-cell',
    },
    {
      key: 'created',
      header: 'Created',
      accessor: (row) => row.CreatedAt || 'N/A',
      cellClassName: 'date-cell',
    },
    {
      key: 'completed',
      header: 'Completed',
      accessor: (row) => row.CompletedAt || 'N/A',
      cellClassName: 'date-cell',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) return [];
    
    // Normalize user IDs for comparison (handle both string and number)
    const userId = String(userData.id || userData.userId || '');
    
    const userRoles = row.TaskMembers?.filter(member => {
      const memberUserId = String(member.user_id || member.userId || '');
      return memberUserId === userId;
    }).map(member => member.role) || [];

    const isCreator = userRoles.includes('creator');
    const isAssignee = userRoles.includes('assignee');
    const isObserver = userRoles.includes('observer');

    let isDepartmentAllowed = false;
    let isBranchAllowed = false;
    row.Departments?.forEach(department => {
      const deptId = department.department_id || department.departmentId;
      const userDeptId = userData.departmentId || userData.department_id;
      if (String(deptId) === String(userDeptId) && department.is_assignee == true) {
        isDepartmentAllowed = true;
      }
    });
    row.Branches?.forEach(branch => {
      const branchId = branch.branch_id || branch.branchId;
      const userBranchId = userData.branchId || userData.branch_id;
      if (String(branchId) === String(userBranchId) && branch.is_assignee == true) {
        isBranchAllowed = true;
      }
    });

    const canEdit = isCreator || ((isAssignee || isDepartmentAllowed || isBranchAllowed) && row.is_assignee_update_allowed);
    
    if (!canEdit) {
      return [];
    }

    const actions = [
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: (task) => {
          const event = { stopPropagation: () => {} };
          handleEdit(event, task);
        },
      },
    ];

    if (isCreator) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        className: 'text-danger',
        onClick: (task) => handleDeleteWithConfirmation(task),
      });
    }

    return actions;
  };

  // Render list view table
  const renderListView = () => {
    const emptyMessage = searchText 
      ? 'No tasks match your search criteria.' 
      : 'Get started by creating your first task.';

    return (
      <CommonTable
        columns={columns}
        data={tasks || []}
        onRowClick={(row, rowIndex, e) => TaskUpdate(e, row)}
        actions={getActions}
        emptyMessage={emptyMessage}
        emptyStateIcon="fas fa-tasks"
        getRowId={(row, index) => row.TasksId || index}
        maxHeight="85vh"
        minHeight="530px"
      />
    );
  };

  return (
    <div className="task-list-container">
      <ToastContainer />

      {/* Search Section */}
      <div className="search-section">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-input-group">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="form-control"
              placeholder="Search tasks..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSearchParams(prev => {
                  const params = new URLSearchParams(prev);
                  if (e.target.value) {
                    params.set('search', e.target.value);
                  } else {
                    params.delete('search');
                  }
                  return params;
                });
              }}
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="sort-dropdown task-list-sort">
              <Dropdown>
                <Dropdown.Toggle 
                  variant="" 
                  as="button"
                  style={{
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    color: '#C42126',
                    background: 'transparent',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.background = '#f8f9fa';
                    e.target.style.color = '#C42126';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#C42126';
                  }}
                >
                  <i className="fas fa-sort"></i>
                  {sortBy === 'creation' ? 'Creation Date' : 
                   sortBy === 'deadline' ? 'Deadline' : 
                   sortBy === 'priority' ? 'Priority' : 
                   sortBy === 'rank' ? 'Default' : 'Sort by'}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => handleSortChange('rank')}>
                    Default
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('creation')}>
                    Creation Date
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('deadline')}>
                    Deadline
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('priority')}>
                    Priority
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            {/* View Toggle Button */}
            <div className="view-toggle-container">
              <button
                className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('card')}
                title="Card View"
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => handleViewModeChange('list')}
                title="List View"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
            <button
              className="btn"
              onClick={() => setSearchDropDown(true)}
              style={{
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                border: 'none',
                color: '#C42126',
                background: 'transparent',
                fontWeight: '600',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.background = '#f8f9fa';
                e.target.style.color = '#C42126';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.background = 'transparent';
                e.target.style.color = '#C42126';
              }}
            >
              <i className="fas fa-filter me-2"></i>
              Filters
            </button>
            {hasAddTaskAccess ? (
              <Link to={`/task/addtask${getTaskListQuerySuffix()}`} className="new-task-btn" onClick={handleAddTask}>
                <i className="fas fa-plus me-2"></i>
                New Task
              </Link>
            ) : (
              <button 
                className="new-task-btn locked-btn" 
                onClick={handleAddTask}
                title="Adding tasks is not available in your current plan"
              >
                <i className="fas fa-lock me-2"></i>
                New Task
              </button>
            )}
          </div>
        </div>
      </div>
      {searchDropDown && (
        <div className="filter-wrapper" ref={filterRef}>
          <div className="content">
            <div className="quick-filters">
                      {(quickFilters || []).filter(Boolean).map((filter) => (
                <div
                  key={filter}
                  name='Priority'
                  className={`quick-filter ${selectedQuickFilter === filter ? 'active' : ''}`}
                  onClick={() => setSelectedQuickFilter(filter)}
                >
                  {filter}
                </div>
              ))}
            </div>

            <div className="main-filters">
              <div className="filter-form">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Project</label>
                    <select
                      name="ProjectId"
                      value={filters.ProjectId}
                      onChange={handleChange}
                    >
                      <option value="">Select Project</option>
                      {(Projects || []).filter(p => p && p.id != null && p.name).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleChange}
                    >
                      <option value="">Select Status</option>
                      <option>Pending</option>
                      <option>In Progress</option>
                      <option>Blocked</option>
                      <option>Completed</option>
                      <option>Paused</option>
                      <option>Partial</option>
                    </select>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label>Task Date Range</label>
                    <DateRangePicker
                      initialSettings={{
                        ...(filters.startDateFrom && filters.startDateTo ? {
                          startDate: filters.startDateFrom,
                          endDate: filters.startDateTo,
                        } : {}),
                        locale: { format: 'MM/DD/YYYY' },
                        autoApply: true,
                      }}
                      onCallback={(start, end) => {
                        setFilters({
                          ...filters,
                          startDateFrom: start.toDate(),
                          startDateTo: end.toDate(),
                        });
                      }}
                      onApply={(event, picker) => {
                        if (picker.startDate && picker.endDate) {
                          setFilters({
                            ...filters,
                            startDateFrom: picker.startDate.toDate(),
                            startDateTo: picker.endDate.toDate(),
                          });
                        }
                      }}
                    >
                      <input
                        type="text"
                        className="form-control input-daterange-timepicker"
                        readOnly
                        placeholder="Select task date range"
                      />
                    </DateRangePicker>
                  </div>

                  <div className="filter-group">
                    <label>End Date Range</label>
                    <DateRangePicker
                      initialSettings={{
                        ...(filters.endDateFrom && filters.endDateTo ? {
                          startDate: filters.endDateFrom,
                          endDate: filters.endDateTo,
                        } : {}),
                        locale: { format: 'MM/DD/YYYY' },
                        autoApply: true,
                      }}
                      onCallback={(start, end) => {
                        setFilters({
                          ...filters,
                          endDateFrom: start.toDate(),
                          endDateTo: end.toDate(),
                        });
                      }}
                      onApply={(event, picker) => {
                        if (picker.startDate && picker.endDate) {
                          setFilters({
                            ...filters,
                            endDateFrom: picker.startDate.toDate(),
                            endDateTo: picker.endDate.toDate(),
                          });
                        }
                      }}
                    >
                      <input
                        type="text"
                        className="form-control input-daterange-timepicker"
                        readOnly
                        placeholder="Select end date range"
                      />
                    </DateRangePicker>
                  </div>
                </div>

                <div className="checkbox-filter-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="only_my_task"
                      checked={filters.only_my_task}
                      onChange={handleChange}
                      className="checkbox-input"
                    />
                    <span>Only My Tasks</span>
                  </label>
                </div>
              </div>

              <div className="filter-actions">
                <button className="search-btn" onClick={handleSearch}>
                  <i className="fas fa-search me-2"></i>
                  Search
                </button>
                <button className="reset-btn" onClick={resetFilters}>
                  <i className="fas fa-undo me-2"></i>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : viewMode === 'list' ? (
        renderListView()
      ) : (
        <div className="row row-list">
          {tasks && tasks.length > 0 ? (
            (tasks || []).filter(Boolean).map((task, index) => {
              const userData = JSON.parse(localStorage.getItem("user"));
              if (!userData) return null;

              const userId = String(userData.id || userData.userId || '');

              const userRoles = (task.TaskMembers || [])
                .filter((member) => {
                  if (!member) return false;
                  const memberUserId = String(member.user_id || member.userId || '');
                  return memberUserId === userId;
                })
                .map((member) => member.role) || [];

              const isCreator = userRoles.includes('creator');
              const isAssignee = userRoles.includes('assignee');

              let isDepartmentAllowed = false;
              let isBranchAllowed = false;
              (task.Departments || []).forEach((department) => {
                if (department) {
                  const deptId = department.department_id || department.departmentId;
                  const userDeptId = userData.departmentId || userData.department_id;
                  if (String(deptId) === String(userDeptId) && department.is_assignee == true) {
                    isDepartmentAllowed = true;
                  }
                }
              });
              (task.Branches || []).forEach((branch) => {
                if (branch) {
                  const branchId = branch.branch_id || branch.branchId;
                  const userBranchId = userData.branchId || userData.branch_id;
                  if (String(branchId) === String(userBranchId) && branch.is_assignee == true) {
                    isBranchAllowed = true;
                  }
                }
              });

              // Get priority badge class
              const getPriorityBadgeClass = (priority) => {
                switch (priority?.toLowerCase()) {
                  case 'high': return 'priority-badge priority-high';
                  case 'medium': return 'priority-badge priority-medium';
                  case 'low': return 'priority-badge priority-low';
                  default: return 'priority-badge priority-low';
                }
              };

              // Get status badge class
              const getStatusBadgeClass = (status) => {
                // Handle numeric status values
                switch (parseInt(status)) {
                  case 0: return 'status-badge status-pending';
                  case 1: return 'status-badge status-ongoing';
                  case 2: return 'status-badge status-pending';
                  case 3: return 'status-badge status-completed';
                  case 4: return 'status-badge status-pending';
                  case 5: return 'status-badge status-ongoing';
                  default: return 'status-badge status-not-started';
                }
              };

              return (
                <div className="col-xl-3 col-xxl-4 col-lg-6 col-md-6 col-sm-6 mb-4" key={index}>
                  <div className="task-card" onClick={(e) => TaskUpdate(e, task)}>
                    <div className="task-card-header">
                      <div>
                        <div className="task-id">#{task.TasksId}</div>
                        <h6 className="task-title">
                          {task.TaskName}
                        </h6>
                        <div className="task-meta">
                          <i className="fas fa-calendar-alt"></i>
                          <span>Created on {task.CreatedAt}</span>
                        </div>
                      </div>

                      {/* Role-based Dropdown: Show for creators and assignees (when allowed) */}
                      {(isCreator || ((isAssignee || isDepartmentAllowed || isBranchAllowed) && task.is_assignee_update_allowed) ) && (
                        <div className="task-dropdown">
                          <Dropdown 
                            onClick={(e) => e.stopPropagation()}
                            onToggle={(isOpen) => {
                              if (isOpen) {
                                setOpenDropdownId(`card-${task.TasksId}`);
                              } else {
                                setOpenDropdownId(null);
                              }
                            }}
                            show={openDropdownId === `card-${task.TasksId}`}
                          >
                            <Dropdown.Toggle variant="" as="div" className="btn-link">
                              <i className="fas fa-ellipsis-v"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              {/* Edit: Creator and Assignee (only if allowed) */}
                              <Dropdown.Item onClick={(e) => {
                                handleEdit(e, task);
                                setOpenDropdownId(null);
                              }}>
                                <i className="fas fa-edit me-2"></i>
                                Edit
                              </Dropdown.Item>
                              {/* Delete: Only Creator */}
                              {isCreator && (
                                <Dropdown.Item className="text-danger" onClick={() => {
                                  handleDeleteWithConfirmation(task);
                                  setOpenDropdownId(null);
                                }}>
                                  <i className="fas fa-trash me-2"></i>
                                  Delete
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      )}
                    </div>

                    <div className="task-card-body">
                      <ul className="task-info-list">
                        <li className="task-info-item">
                          <span className="task-info-label">Priority</span>
                          <span className={getPriorityBadgeClass(task.Priority)}>
                            {task.Priority || 'Low'}
                          </span>
                        </li>
                        <li className="task-info-item">
                          <span className="task-info-label">Status</span>
                          <span className={getStatusBadgeClass(task.status)}>
                            {(() => {
                              // Handle numeric status values
                              const taskStatus = parseInt(task.status);
                              switch (taskStatus) {
                                case 0: return 'Pending';
                                case 1: return 'In Progress';
                                case 2: return 'Blocked';
                                case 3: return 'Completed';
                                case 4: return 'Paused';
                                case 5: return 'Partial';
                                default: return 'To Do';
                              }
                            })()}
                          </span>
                        </li>
                        <li className="task-info-item">
                          <span className="task-info-label">Task Date</span>
                          <span className="task-info-value">{task.StartDate}</span>
                        </li>
                        <li className="task-info-item">
                          <span className="task-info-label">End Date</span>
                          <span className={`task-info-value ${isTaskOverdue(task) ? 'overdue-date' : ''}`}>
                            {task.EndDate}
                            {isTaskOverdue(task) && (
                              <span className="overdue-indicator ms-1" title="This task is overdue">
                                <i className="fas fa-exclamation-triangle"></i>
                              </span>
                            )}
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* Debug info - remove this after fixing */}
                    {/* <div style={{fontSize: '10px', color: '#666', padding: '5px'}}>
            User Roles: {userRoles.join(', ')} | Task ID: {task.TasksId}
          </div> */}
                  </div>
                </div>
              );
            }).filter(Boolean)
          ) : (
            <div className="col-12">
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="fas fa-tasks"></i>
                </div>
                <h3 className="empty-state-title">No Tasks Found</h3>
                <p className="empty-state-description">
                  {searchText ? 'No tasks match your search criteria.' : 'Get started by creating your first task.'}
                </p>
                {!searchText && (
                  hasAddTaskAccess ? (
                    <Link to={`/task/addtask${getTaskListQuerySuffix()}`} className="new-task-btn mt-3" onClick={handleAddTask}>
                      <i className="fas fa-plus me-2"></i>
                      Create Your First Task
                    </Link>
                  ) : (
                    <div className="text-center">
                      <p className="text-muted mb-3 small">
                        Upgrade your plan to start managing your tasks.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate('/user-subscription')}
                      >
                        <i className="fas fa-crown me-2"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {loadingMore && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => {
        setShowDeleteModal(false);
        setDeleteTaskData(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {deleteTaskData?.repeated_from_task_id ? 'Delete Repeat Task Instance' : 
             deleteTaskData?.is_repeatable ? 'Delete Repeat Task (Root)' : 'Delete Task'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(deleteTaskData?.repeated_from_task_id || deleteTaskData?.is_repeatable) ? (
            <>
              <p>{deleteTaskData?.repeated_from_task_id ? 'This is a repeat task instance.' : 'This is a repeat task root.'} What would you like to delete?</p>
              <div className="mt-3">
                <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #6c757d' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#495057' }}>
                    {deleteTaskData?.repeated_from_task_id ? 'Delete This Instance Only' : 'Delete This Task Only'}
                  </strong>
                  <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                    {deleteTaskData?.repeated_from_task_id
                      ? 'Remove only this specific task instance.'
                      : 'Remove only this root task; the repeat series will continue from the next occurrence.'}
                  </span>
                </div>
                <div className="p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '4px', borderLeft: '4px solid #ffc107' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#856404' }}>Delete Entire Series</strong>
                  <span style={{ color: '#6c757d', fontSize: '0.9em' }}>Remove all instances of this repeat task, including future ones.</span>
                </div>
              </div>
            </>
          ) : (
            <p>Are you sure you want to delete this task? Once deleted, you will not be able to recover this task!</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteTaskData(null);
            }}
          >
            Cancel
          </Button>
          {(deleteTaskData?.repeated_from_task_id || deleteTaskData?.is_repeatable) ? (
            <>
              <Button 
                className="doc-form-submit-btn"
                onClick={handleDeleteInstance}
              >
                {deleteTaskData?.repeated_from_task_id ? 'Delete Instance Only' : 'Delete This Task Only'}
              </Button>
              <Button 
                className="doc-form-submit-btn"
                onClick={handleDeleteSeries}
              >
                Delete Entire Series
              </Button>
            </>
          ) : (
            <Button 
              className="doc-form-submit-btn"
              onClick={handleDeleteNormal}
            >
              Delete
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Confirmation Modal for Repeat Task (instance and root) - same pattern as delete */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setEditTaskData(null);
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editTaskData?.repeated_from_task_id ? 'Edit Repeat Task Instance' : 'Edit Repeat Task (Root)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(editTaskData?.repeated_from_task_id || editTaskData?.is_repeatable) && (
            <>
              <p>{editTaskData?.repeated_from_task_id ? 'This is a repeat task instance.' : 'This is a repeat task root.'} What would you like to edit?</p>
              <div className="mt-3">
                <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #6c757d' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#495057' }}>
                    {editTaskData?.repeated_from_task_id ? 'Edit This Instance Only' : 'Edit This Task Only'}
                  </strong>
                  <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                    {editTaskData?.repeated_from_task_id
                      ? 'Edit only this specific task instance.'
                      : 'Edit only this root task; changes will apply to the series definition and affect all instances.'}
                  </span>
                </div>
                <div className="p-3" style={{ backgroundColor: '#e7f3ff', borderRadius: '4px', borderLeft: '4px solid #007bff' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#004085' }}>Edit Entire Series</strong>
                  <span style={{ color: '#6c757d', fontSize: '0.9em' }}>Edit the root task, which will affect all instances of this repeat task.</span>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="doc-form-cancel-btn"
            onClick={() => {
              setShowEditModal(false);
              setEditTaskData(null);
            }}
          >
            Cancel
          </Button>
          {(editTaskData?.repeated_from_task_id || editTaskData?.is_repeatable) && (
            <>
              <Button 
                className="doc-form-submit-btn"
                onClick={handleEditInstance}
              >
                {editTaskData?.repeated_from_task_id ? 'Edit Instance Only' : 'Edit This Task Only'}
              </Button>
              <Button 
                className="doc-form-submit-btn"
                onClick={handleEditSeries}
              >
                Edit Entire Series
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <InternalFooter />
    </div>
  );
}

export default TaskList;